/**
 * PeopleYouMayKnow Component
 * ==========================
 * Displays suggested connections based on social graph analysis.
 * 
 * This is a MOAT FEATURE - drives engagement and stickiness.
 * 
 * Usage:
 *   <PeopleYouMayKnow userId="..." session={session} />
 */

import { useState, useEffect } from 'react';
import { Users, UserPlus, Sparkles, ChevronRight, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';

// Connection strength colors
const STRENGTH_CONFIG = {
  strong: {
    color: 'text-emerald-400',
    bgColor: 'bg-emerald-500/20',
    label: 'Strong Connection',
  },
  moderate: {
    color: 'text-cyan-400',
    bgColor: 'bg-cyan-500/20',
    label: 'Good Match',
  },
  weak: {
    color: 'text-amber-400',
    bgColor: 'bg-amber-500/20',
    label: 'Potential Connection',
  },
  suggested: {
    color: 'text-slate-400',
    bgColor: 'bg-slate-500/20',
    label: 'Suggested',
  },
};

// Hook to fetch suggestions
export const useSuggestedConnections = (userId, session, limit = 10) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userId || !session?.access_token) {
      setLoading(false);
      return;
    }

    const fetchSuggestions = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/social-graph/user/${userId}/suggested-connections?limit=${limit}`,
          {
            headers: {
              'Authorization': `Bearer ${session.access_token}`,
              'Content-Type': 'application/json',
            },
          }
        );
        
        if (response.ok) {
          const result = await response.json();
          setData(result);
        } else {
          setError('Failed to load suggestions');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSuggestions();
  }, [userId, session?.access_token, limit]);

  return { data, loading, error };
};

// Suggestion Card Component
const SuggestionCard = ({ suggestion, onConnect, className }) => {
  const strength = STRENGTH_CONFIG[suggestion.connection_strength] || STRENGTH_CONFIG.suggested;
  
  return (
    <div 
      className={cn(
        'flex items-center gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/[0.06]',
        'hover:bg-white/[0.04] hover:border-white/[0.1] transition-all group',
        className
      )}
      data-testid={`suggestion-card-${suggestion.user_id}`}
    >
      {/* Avatar */}
      <Avatar className="h-12 w-12 border-2 border-white/10">
        <AvatarImage src={suggestion.avatar_url} />
        <AvatarFallback className="bg-white/10 text-white/60">
          {suggestion.display_name?.charAt(0) || '?'}
        </AvatarFallback>
      </Avatar>
      
      {/* Info */}
      <div className="flex-1 min-w-0">
        <h4 className="font-medium text-white truncate">
          {suggestion.display_name || 'Anonymous'}
        </h4>
        
        {/* Connection reason (first one) */}
        {suggestion.reasons?.[0] && (
          <p className="text-xs text-white/50 truncate">
            {suggestion.reasons[0]}
          </p>
        )}
        
        {/* Connection strength badge */}
        <Badge 
          className={cn(
            'mt-1 text-[10px] px-1.5 py-0 h-4',
            strength.bgColor,
            strength.color,
            'border-0'
          )}
        >
          {strength.label}
        </Badge>
      </div>
      
      {/* Action */}
      <Button
        variant="ghost"
        size="sm"
        className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={() => onConnect?.(suggestion)}
      >
        <UserPlus className="w-4 h-4 text-white/50 hover:text-cyan-400" />
      </Button>
    </div>
  );
};

// Main Component
export const PeopleYouMayKnow = ({
  userId,
  session,
  limit = 5,
  showHeader = true,
  onViewAll,
  onConnect,
  className,
}) => {
  const { data, loading, error } = useSuggestedConnections(userId, session, limit);
  
  if (loading) {
    return (
      <div className={cn('space-y-3', className)}>
        {showHeader && (
          <div className="flex items-center gap-2 text-white/60">
            <Users className="w-4 h-4" />
            <span className="text-sm font-medium">People You May Know</span>
          </div>
        )}
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 text-white/30 animate-spin" />
        </div>
      </div>
    );
  }
  
  if (error || !data?.suggestions?.length) {
    return null; // Silently hide if no suggestions
  }
  
  const suggestions = data.suggestions || [];
  
  return (
    <div 
      className={cn('space-y-3', className)}
      data-testid="people-you-may-know"
    >
      {/* Header */}
      {showHeader && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500/20 to-cyan-500/20">
              <Sparkles className="w-4 h-4 text-violet-400" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-white">People You May Know</h3>
              <p className="text-xs text-white/40">Based on your activity</p>
            </div>
          </div>
          
          {onViewAll && suggestions.length >= limit && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onViewAll}
              className="text-xs text-white/50 hover:text-white"
            >
              View All
              <ChevronRight className="w-3 h-3 ml-1" />
            </Button>
          )}
        </div>
      )}
      
      {/* Suggestions List */}
      <div className="space-y-2">
        {suggestions.slice(0, limit).map((suggestion) => (
          <SuggestionCard
            key={suggestion.user_id}
            suggestion={suggestion}
            onConnect={onConnect}
          />
        ))}
      </div>
      
      {/* Stats (optional) */}
      {data.total_candidates_analyzed > 0 && (
        <p className="text-[10px] text-white/30 text-center pt-2">
          Analyzed {data.total_candidates_analyzed} potential connections
        </p>
      )}
    </div>
  );
};

// Compact variant for sidebars
export const PeopleYouMayKnowCompact = ({
  userId,
  session,
  limit = 3,
  onViewAll,
  className,
}) => {
  const { data, loading } = useSuggestedConnections(userId, session, limit);
  
  if (loading || !data?.suggestions?.length) {
    return null;
  }
  
  return (
    <div 
      className={cn(
        'p-4 rounded-xl bg-gradient-to-br from-violet-500/10 to-cyan-500/10 border border-white/[0.06]',
        className
      )}
      data-testid="people-you-may-know-compact"
    >
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4 text-violet-400" />
        <span className="text-sm font-medium text-white">Suggested Connections</span>
      </div>
      
      {/* Avatar stack */}
      <div className="flex items-center gap-2">
        <div className="flex -space-x-2">
          {data.suggestions.slice(0, 4).map((s, i) => (
            <Avatar 
              key={s.user_id} 
              className="h-8 w-8 border-2 border-obsidian"
              style={{ zIndex: 4 - i }}
            >
              <AvatarImage src={s.avatar_url} />
              <AvatarFallback className="bg-white/10 text-white/60 text-xs">
                {s.display_name?.charAt(0) || '?'}
              </AvatarFallback>
            </Avatar>
          ))}
        </div>
        
        <span className="text-xs text-white/50">
          {data.suggestions.length}+ people
        </span>
        
        {onViewAll && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onViewAll}
            className="ml-auto h-6 text-xs text-cyan-400"
          >
            See All
          </Button>
        )}
      </div>
    </div>
  );
};

export default PeopleYouMayKnow;
